import apiClient from './api';
import type {
  Organisation,
  CreateOrganisationDto,
  UpdateOrganisationDto,
} from '@/types/organisation';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrgUser {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  createdAt: string;
  is2FAEnabled: boolean;
}

export interface RegisterOrganisationDto {
  orgName: string;
  slug?: string;
  billingEmail?: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  referralCode?: string;
}

// ---------------------------------------------------------------------------
// Organisations API
// ---------------------------------------------------------------------------

/**
 * List all organisations. ADMIN only.
 */
export async function getOrganisations(): Promise<Organisation[]> {
  const { data } = await apiClient.get<Organisation[]>('/organisations');
  return data;
}

/**
 * Get a single organisation by ID. ADMIN only.
 */
export async function getOrganisation(id: number): Promise<Organisation> {
  const { data } = await apiClient.get<Organisation>(`/organisations/${id}`);
  return data;
}

/**
 * Create a new organisation. ADMIN only.
 */
export async function createOrganisation(dto: CreateOrganisationDto): Promise<Organisation> {
  const { data } = await apiClient.post<Organisation>('/organisations', dto);
  return data;
}

/**
 * Update an organisation. ADMIN only.
 */
export async function updateOrganisation(
  id: number,
  dto: UpdateOrganisationDto
): Promise<Organisation> {
  const { data } = await apiClient.patch<Organisation>(`/organisations/${id}`, dto);
  return data;
}

/**
 * Delete an organisation. ADMIN only.
 */
export async function deleteOrganisation(id: number): Promise<void> {
  await apiClient.delete(`/organisations/${id}`);
}

/**
 * List users belonging to an organisation.
 */
export async function getOrgUsers(orgId: number): Promise<OrgUser[]> {
  const { data } = await apiClient.get<OrgUser[]>(`/organisations/${orgId}/users`);
  return data;
}

// ---------------------------------------------------------------------------
// Public Website API management
// ---------------------------------------------------------------------------

export interface PublicApiSettings {
  isPublicApiEnabled: boolean;
  isVerified: boolean;
  slug: string;
  publicApiKeyPrefix: string | null;
  publicApiKeyLastUsed: string | null;
  allowedDomains: string[];
  whatsappNumber: string | null;
  privacyPolicyUrl: string | null;
  rateLimitReadsPerMin: number | null;
  rateLimitWritesPerMin: number | null;
  rateLimitDailyTotal: number | null;
  maxViewingsPerSlot: number;
}

export interface PublicApiUsage {
  requestsLast30Days: number | null;
  inquiriesLast30Days: number;
  errorRatePct: number | null;
}

export interface EnablePublicApiResult {
  slug: string;
  publicApiKey: string;
  publicApiKeyPrefix: string;
}

export interface RotateKeyResult {
  publicApiKey: string;
  publicApiKeyPrefix: string;
}

export const publicApiService = {
  getSettings: async (orgId: number): Promise<PublicApiSettings> => {
    const { data } = await apiClient.get<PublicApiSettings>(`/organisations/${orgId}/public-api`);
    return data;
  },
  enable: async (orgId: number): Promise<EnablePublicApiResult> => {
    const { data } = await apiClient.post<EnablePublicApiResult>(
      `/organisations/${orgId}/public-api/enable`
    );
    return data;
  },
  disable: async (orgId: number): Promise<void> => {
    await apiClient.delete(`/organisations/${orgId}/public-api/disable`);
  },
  rotateKey: async (orgId: number): Promise<RotateKeyResult> => {
    const { data } = await apiClient.post<RotateKeyResult>(
      `/organisations/${orgId}/public-api/key`
    );
    return data;
  },
  revokeKey: async (orgId: number): Promise<void> => {
    await apiClient.delete(`/organisations/${orgId}/public-api/key`);
  },
  updateDomains: async (
    orgId: number,
    domains: string[]
  ): Promise<{ allowedDomains: string[] }> => {
    const { data } = await apiClient.put<{ allowedDomains: string[] }>(
      `/organisations/${orgId}/public-api/domains`,
      { domains }
    );
    return data;
  },
  updateSlug: async (orgId: number, slug: string): Promise<{ slug: string }> => {
    const { data } = await apiClient.patch<{ slug: string }>(
      `/organisations/${orgId}/public-api/slug`,
      { slug }
    );
    return data;
  },
  updateMaxViewingsPerSlot: async (
    orgId: number,
    maxViewingsPerSlot: number
  ): Promise<{ maxViewingsPerSlot: number }> => {
    const { data } = await apiClient.patch<{ maxViewingsPerSlot: number }>(
      `/organisations/${orgId}/public-api/max-viewings-per-slot`,
      { maxViewingsPerSlot }
    );
    return data;
  },
  getUsage: async (orgId: number): Promise<PublicApiUsage> => {
    const { data } = await apiClient.get<PublicApiUsage>(
      `/organisations/${orgId}/public-api/usage`
    );
    return data;
  },
};

/**
 * Public self-onboarding, creates an org + admin user in one step.
 * No auth token required.
 */
export async function registerOrganisation(
  dto: RegisterOrganisationDto
): Promise<{ message: string; orgId: number; orgName: string; userId: number }> {
  const { data } = await apiClient.post('/auth/register', dto);
  return data;
}
